import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * Case content in the officer's language (visual review item 1). Content a party, a source or an agent wrote in English stays stored as
 * written; on an Arabic screen:
 *  - economic abbreviations read as their Arabic terms (HHI → مؤشر هيرفندال-هيرشمان …) and programme values as words;
 *  - demonstration tags never show;
 *  - an English passage of three words or more is shown in an Arabic machine translation, marked as such, with the original one hover away
 *    (the app supplies the translator; translations are made once and cached on the server);
 *  - any English that remains is isolated, so the line keeps its order ("xlsx.IMF growth outlook" no longer flips).
 */

/** Abbreviations and programme values, longest first so ΔHHI wins over HHI. */
const GLOSSARY_AR: Array<[RegExp, string]> = [
  [/التغير في مؤشر HHI/g, "التغير في مؤشر هيرفندال-هيرشمان"],
  [/مؤشر HHI/g, "مؤشر هيرفندال-هيرشمان"],
  [/ΔHHI|Δ\s?HHI|delta HHI/gi, "التغير في مؤشر هيرفندال-هيرشمان"],
  [/\bHHI\b/g, "مؤشر هيرفندال-هيرشمان"],
  [/\bCR4\b/g, "نسبة تركز أكبر أربع منشآت"],
  [/\bCR3\b/g, "نسبة تركز أكبر ثلاث منشآت"],
  [/\bCMCR\b/g, "الخفض التعويضي في التكلفة الحدية"],
  [/\bGUPPI\b/g, "مؤشر الضغط التصاعدي على الأسعار"],
  [/\bUPP\b/g, "الضغط التصاعدي على الأسعار"],
  [/\bSSNIP\b/g, "اختبار الزيادة الصغيرة والمعتبرة وغير المؤقتة في السعر"],
  [/\bISIC\b/g, "التصنيف الصناعي الدولي"],
  [/\bAED\b/g, "درهم"],
  [/\bNotSubjectToNotification\b/g, "غير خاضع للإخطار"],
  [/\bSubjectToNotification\b/g, "خاضع للإخطار"],
  [/\bDoesNotConstitute\b/g, "لا يشكل تركزا"],
  [/\bConstitutes\b/g, "يشكل تركزا"],
  [/\bNotNotifiable\b/g, "غير خاضع للإخطار"],
  [/\bApproveWithConditions\b/g, "الموافقة بشروط"],
  [/\bInternational Digest\b/g, "الموجز الاقتصادي الدولي"],
];

/**
 * Terms the Department replaced (7.24, visual item 4) as they read in titles and journal lines recorded before: shown with today's term.
 * Documents are shown as they were approved (2.7); only names, titles and journal lines take the current wording.
 */
const RECORDED_TERMS_AR: Array<[RegExp, string]> = [
  [/مراجعة مستقلة لتقرير التحقيق من خبير اقتصادي من خارج فريق الحالة/g, "مراجعة مستقلة لتقرير الدراسة من خبير من خارج فريق الملف"],
  [/كاتب تقرير التحقيق في التركز الاقتصادي/g, "كاتب تقرير الدراسة"],
  [/تقرير التحقيق في عملية تركز اقتصادي/g, "تقرير دراسة عملية التركز الاقتصادي"],
  [/المتعاملين/g, "مقدمي الطلبات"],
  [/المتعامل/g, "مقدم الطلب"],
  [/فريق الحالة/g, "فريق الملف"],
  [/تم إنشاء الحالة/g, "أنشئ الملف"],
  [/Investigation report on an economic concentration/g, "تقرير دراسة عملية التركز الاقتصادي"],
  // Names the platform itself records for a party or an author, when they are the whole text.
  [/^Competition Department$/, "إدارة المنافسة"],
  [/^Anonymous reporter$/, "مبلغ مجهول الهوية"],
  [/\s*\(?\u2068?MOE\/CD-F-\d+\u2069?\)?/g, ""],
];

/** Today's terms in a recorded title or journal line (Arabic screens). */
export function recordedTerms(s: string): string {
  let out = s;
  for (const [re, ar] of RECORDED_TERMS_AR) out = out.replace(re, ar);
  return out;
}

/** Tags that marked demonstration content, and the seed's reference codes quoted in recorded summaries; they are never shown. */
const DEMO_TAGS = /\s*\((?:fictional[^)]*|demo|synthetic[^)]*|نموذج تجريبي|(?:Ref|Reference|المرجع|مرجع)[:.]?\s*\u2068?[A-Z]{2}-DEMO-[A-Z0-9-]+[^)]*)\)/gi;

/** A run of Latin-script words (with the punctuation names and phrases carry). */
export const LATIN_RUN = /([A-Za-z][\w&'’.\-/%]*(?:[  ,()]+[A-Za-z0-9][\w&'’.\-/%]*\)?)*)/;
const LATIN_RUN_G = new RegExp(LATIN_RUN.source, "g");

export const stripDemoTags = (s: string) => s.replace(DEMO_TAGS, "");

/** The Arabic terms for the abbreviations and programme values in a text (Arabic screens only). */
export function arabicTerms(s: string): string {
  let out = s;
  for (const [re, ar] of GLOSSARY_AR) out = out.replace(re, ar);
  return out;
}

/** True for an English run long enough to be prose or a title rather than a name or a code (three words or more). */
export function isPassage(run: string): boolean {
  // Longer passages (whole documents) stay as written: they are read in the document viewer, not translated in part.
  if (run.length > 1500 || /:\/\/|@|\.(?:xlsx?|pdf|docx?|csv)\b/i.test(run)) return false;
  // A company's registered name stays a name (it is isolated and read as written): legal suffixes, or a registry's capitals.
  if (!/[a-z]/.test(run) || /\b(LLC|L\.L\.C\.?|PJSC|P\.J\.S\.C\.?|Ltd\.?|Limited|PLC|FZ-?LLC|FZE|FZCO|SE|LP|Inc\.?|GmbH|AG|Holding|Holdings|Group|Company)\.?\)?$/.test(run.trim())) return false;
  return run.trim().split(/\s+/).filter((w) => /[A-Za-z]{2,}/.test(w)).length >= 3;
}

type Translate = (texts: string[]) => Promise<Record<string, string>>;
interface ContentLanguage { get: (text: string) => string | undefined; request: (text: string) => void; version: number }
const Ctx = createContext<ContentLanguage | null>(null);

/** Supplies the machine translator to every SourceText and Markdown body below it; requests are batched and each passage asked once. */
export function ContentLanguageProvider({ translate, children }: { translate: Translate; children: ReactNode }) {
  const cache = useRef(new Map<string, string>());
  const asked = useRef(new Set<string>());
  const queue = useRef(new Set<string>());
  const timer = useRef<number | null>(null);
  const [version, setVersion] = useState(0);

  const flush = useCallback(() => {
    timer.current = null;
    const batch = Array.from(queue.current).slice(0, 40);
    batch.forEach((t) => queue.current.delete(t));
    if (batch.length === 0) return;
    translate(batch).then((found) => {
      let changed = false;
      for (const [k, v] of Object.entries(found ?? {})) if (v && !cache.current.has(k)) { cache.current.set(k, v); changed = true; }
      if (changed) setVersion((n) => n + 1);
    }).catch(() => undefined).finally(() => {
      if (queue.current.size > 0 && timer.current == null) timer.current = window.setTimeout(flush, 80);
    });
  }, [translate]);

  const request = useCallback((text: string) => {
    if (cache.current.has(text) || asked.current.has(text)) return;
    asked.current.add(text);
    queue.current.add(text);
    if (timer.current == null) timer.current = window.setTimeout(flush, 80);
  }, [flush]);

  useEffect(() => () => { if (timer.current != null) window.clearTimeout(timer.current); }, []);
  const value = useMemo<ContentLanguage>(() => ({ get: (t) => cache.current.get(t), request, version }), [request, version]);
  return <Ctx.Provider value={value}><PageTranslator lang={value} />{children}</Ctx.Provider>;
}

const SKIP = "script,style,textarea,input,select,code,pre,kbd,[contenteditable=''],[contenteditable='true'],[data-no-translate],[lang='en'][data-keep]";
const ORIGINAL = new WeakMap<Text, string>();

/**
 * On an Arabic screen, English passages that a screen renders as plain text (a party's description, a deal headline, a journal line) are
 * replaced in place by their cached machine translation, the parent marked with a dotted underline and the original on hover. Inputs,
 * editors and code are never touched; the text React renders next replaces ours and is translated again.
 */
function PageTranslator({ lang }: { lang: ContentLanguage }) {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const run = () => {
      if (document.documentElement.lang !== "ar") return;
      const roots = [...document.querySelectorAll("main, [role=dialog], [data-radix-popper-content-wrapper]")];
      for (const root of roots) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
          const value = node.nodeValue ?? "";
          if (!/[A-Za-z]{2,}|المتعامل|فريق الحالة|تقرير التحقيق في/.test(value)) continue;
          const parent = node.parentElement;
          if (!parent || parent.closest(SKIP)) continue;
          // Today's terms and the Arabic names of abbreviations first, then English passages in their translation.
          const termed = recordedTerms(arabicTerms(stripDemoTags(value)));
          let changed = termed !== value;
          const next = termed.replace(LATIN_RUN_G, (r) => {
            if (!isPassage(r)) return r;
            const hit = lang.get(r);
            if (hit) { changed = true; return hit; }
            lang.request(r);
            return r;
          });
          if (changed) {
            ORIGINAL.set(node, value);
            node.nodeValue = next;
            parent.setAttribute("data-mt", "");
            if (!parent.title) parent.title = value.trim();
          }
        }
      }
    };
    let scheduled = 0;
    const schedule = () => { if (!scheduled) scheduled = window.setTimeout(() => { scheduled = 0; run(); }, 150); };
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { subtree: true, childList: true, characterData: true });
    schedule();
    return () => { observer.disconnect(); if (scheduled) window.clearTimeout(scheduled); };
  }, [lang]);
  return null;
}

export function useContentLanguage(): ContentLanguage | null {
  return useContext(Ctx);
}

/**
 * The text of a Markdown body for an Arabic screen: demonstration tags removed, abbreviations as Arabic terms, and English passages replaced
 * by their machine translation once known (asked for otherwise). Code spans and links are left alone.
 */
export function arabicMarkdown(markdown: string, lang: ContentLanguage | null): string {
  const cleaned = arabicTerms(stripDemoTags(markdown));
  if (!lang) return cleaned;
  return cleaned.split(/(`[^`]*`|\]\([^)]*\)|https?:\/\/\S+)/).map((part, i) => {
    if (i % 2 === 1) return part;
    return part.replace(LATIN_RUN_G, (run) => {
      if (!isPassage(run)) return run;
      const hit = lang.get(run);
      if (hit) return hit;
      lang.request(run);
      return run;
    });
  }).join("");
}
